import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  InnerPageHeader, Card, Button, TextInput, EmptyState, ErrorState,
  Alert, Badge, Spinner, useToast, IconButton,
} from '@parsina/framework';
import { Pill, Trash2, AlertTriangle } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatient } from '../../hooks/usePatient.js';
import { bffQuery, bffMutation, errorCode } from '../../lib/bffClient.js';
import {
  newRxItem, dosageText, toMedicationInput, interactionIds, canSweep,
  severityKey, warningTypeKey, sortWarnings, allergenNames, conditionNames,
} from '../../lib/prescription.js';
import {
  CHECK_DRUG_INTERACTION, CREATE_MEDICATION, SWEEP_DEBOUNCE_MS,
  SEVERITY_TINT, saveLabelKey,
} from './Prescription.shared.js';
import DrugCombobox from './DrugCombobox.jsx';
import Panel from '../../components/common/Panel.jsx';
import { PrescriptionSkeletonWeb } from './Prescription.skeleton.jsx';

// نسخه — inherited unchanged from general medicine (CORE.md §10). Dental
// reshapes the visit CLOSURE, not prescribing.
//
// Two rules this screen exists to honour:
//
//  1. The interaction sweep NEVER blocks prescribing. It costs credits, it can
//     be exhausted, and the service fails open when the wallet is unreachable —
//     so a failed sweep is reported next to the prescription and the ثبت button
//     stays live. A dentist who cannot prescribe because a check timed out is a
//     worse outcome than one who prescribes without it.
//  2. The sweep is keyed on Drug.id SLUGS. Passing the Persian display name
//     reaches the rule engine as an unknown drug and returns a clean bill.

// SEVERITY_TINT and the save-phase label live in Prescription.shared.js: the
// mobile surface paints the same three tones and asks twice in the same order.

const RxRow = ({ item, onChange, onRemove }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 10,
        background: CT.surface,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Pill size={16} color={C.teal} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary }}>{item.drug.name}</div>
          <div style={{ fontSize: 11.5, color: C.textMuted }}>
            {[item.drug.genericName, item.drug.category].filter(Boolean).join(' · ')}
          </div>
        </div>
        <IconButton
          variant="ghost"
          size={36}
          icon={<Trash2 size={15} />}
          label={t('prescription.item.remove', { name: item.drug.name })}
          onClick={onRemove}
        />
      </div>

      {/* aria-label on every field, and it NAMES THE DRUG.
          The kit's FormField renders its `label` as a <span> with no htmlFor and
          no id, so the visible label is not attached to the control: without
          this each of these is an unnamed text box. On a four-drug prescription
          that is sixteen unnamed boxes, and «دوز» alone would not say which
          drug's dose is being typed. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <TextInput
          label={t('prescription.item.dose')}
          aria-label={`${item.drug.name} — ${t('prescription.item.dose')}`}
          dir="auto"
          value={item.dose}
          onChange={(e) => onChange({ ...item, dose: e.target.value })}
        />
        <TextInput
          label={t('prescription.item.frequency')}
          aria-label={`${item.drug.name} — ${t('prescription.item.frequency')}`}
          dir="auto"
          value={item.frequency}
          onChange={(e) => onChange({ ...item, frequency: e.target.value })}
        />
        <TextInput
          label={t('prescription.item.duration')}
          aria-label={`${item.drug.name} — ${t('prescription.item.duration')}`}
          dir="auto"
          value={item.duration}
          onChange={(e) => onChange({ ...item, duration: e.target.value })}
        />
        <TextInput
          label={t('prescription.item.note')}
          aria-label={`${item.drug.name} — ${t('prescription.item.note')}`}
          dir="auto"
          value={item.note}
          onChange={(e) => onChange({ ...item, note: e.target.value })}
        />
      </div>
    </div>
  );
};

const PrescriptionScreen = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointmentId') || null;
  const { t, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const toast = useToast();
  const { activeId } = useClinicScope();
  const { patient, loading, error, reload } = usePatient(patientId, activeId);

  const [items, setItems] = React.useState([]);
  // 'idle' → 'confirm' → 'saving'. A prescription is a clinical write; it takes
  // two deliberate clicks, not one stray one.
  const [phase, setPhase] = React.useState('idle');

  const [sweep, setSweep] = React.useState({ loading: false, result: null, failure: null });

  // A STRING, not an array: what the sweep depends on is *which drugs* are on
  // the prescription, and a string compares by value across renders.
  const idsKey = React.useMemo(() => interactionIds(items).join('|'), [items]);
  const sweepable = canSweep(items);

  React.useEffect(() => {
    if (!patient || !sweepable) {
      setSweep({ loading: false, result: null, failure: null });
      return undefined;
    }
    let alive = true;
    setSweep((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      bffQuery(CHECK_DRUG_INTERACTION, {
        // Rebuilt from the key rather than closed over `ids`: the memoised array
        // gets a new identity on every keystroke in a dose field, which would
        // re-fire this effect — and re-bill the sweep — for an edit that is not
        // even an input to it.
        medicationIds: idsKey.split('|').filter(Boolean),
        patientId: patient.id,
        patientConditions: conditionNames(patient),
        patientAllergies: allergenNames(patient),
      })
        .then((data) => {
          if (!alive) return;
          setSweep({ loading: false, result: data?.checkDrugInteraction || null, failure: null });
        })
        .catch((err) => {
          if (!alive) return;
          // Swallowed on purpose — see the rule at the top of this file. The
          // credit case gets its own copy because "top up" is actionable and
          // "unavailable" is not.
          const code = errorCode(err);
          setSweep({
            loading: false,
            result: null,
            failure: code === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable',
          });
        });
    }, SWEEP_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // idsKey rather than `items`: editing a dose must not re-bill the sweep,
    // because the dose is not an input to it.
  }, [idsKey, patient, sweepable]);

  const addDrug = (drug) => {
    setItems((prev) => (prev.some((i) => i.drug.id === drug.id) ? prev : [...prev, newRxItem(drug)]));
    setPhase('idle');
  };
  const updateItem = (next) => {
    setItems((prev) => prev.map((i) => (i.key === next.key ? next : i)));
    setPhase('idle');
  };
  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    setPhase('idle');
  };

  const save = async () => {
    if (phase === 'idle') {
      setPhase('confirm');
      return;
    }
    if (phase !== 'confirm') return;
    setPhase('saving');

    // One mutation per row. allSettled rather than a chain: a formulary row the
    // service rejects must not silently discard the rows after it, and the
    // dentist has to be told exactly how many landed.
    const results = await Promise.allSettled(
      items.map((item) =>
        bffMutation(CREATE_MEDICATION, {
          patientId: patient.id,
          input: toMedicationInput(item, { appointmentId }),
        }),
      ),
    );
    const saved = results.filter((r) => r.status === 'fulfilled').length;

    if (saved === items.length) {
      toast.success(t('prescription.saved'));
      navigate('patient', { id: patient.id, tab: 'history' });
      return;
    }
    if (saved === 0) {
      toast.error(t('prescription.saveFailed'));
    } else {
      toast.warn(t('prescription.savePartial', { saved, total: items.length }));
    }
    setPhase('idle');
  };

  // The screen's OWN shape while the record loads — header band, the search
  // card and the two rail panels at their real size — so the search field the
  // dentist is about to type into does not move (DESIGN.md §"Loading &
  // skeleton").
  if (loading && !patient) {
    return <PrescriptionSkeletonWeb label={t('common.loading')} />;
  }

  if (!patient) {
    return (
      <div style={{ height: '100%', background: C.bg, padding: 32 }} role="alert">
        <Card>
          <ErrorState
            title={t('patient.error.title')}
            description={error ? t('errors.load.patient') : t('patient.notFound.description')}
            onRetry={error ? reload : undefined}
            retryLabel={t('common.actions.retry')}
          />
        </Card>
      </div>
    );
  }

  const allergies = patient.allergies || [];
  const warnings = sortWarnings(sweep.result?.warnings);
  const saveLabel = t(saveLabelKey(phase));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <InnerPageHeader
        onBack={() => navigate('patient', { id: patient.id })}
        backLabel={t('common.actions.back')}
        title={t('prescription.title')}
        sub={t('prescription.subtitle', { name: patient.name })}
      >
        <Button onClick={save} disabled={items.length === 0 || phase === 'saving'}>
          {saveLabel}
        </Button>
      </InnerPageHeader>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 40px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 320px',
            gap: 18,
            alignItems: 'start',
          }}
        >
          <div>
            <Card style={{ marginBottom: 14, overflow: 'visible' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, marginBottom: 8 }}>
                {t('prescription.search.label')}
              </div>
              <DrugCombobox onAdd={addDrug} />
            </Card>

            <div data-testid="rx-items" data-state={items.length ? 'content' : 'empty'}>
              {items.length === 0 ? (
                <Card>
                  <EmptyState
                    icon={<Pill size={44} color={C.textMuted} />}
                    title={t('prescription.empty.title')}
                    description={t('prescription.empty.description')}
                  />
                </Card>
              ) : (
                items.map((item) => (
                  <RxRow
                    key={item.key}
                    item={item}
                    onChange={updateItem}
                    onRemove={() => removeItem(item.key)}
                  />
                ))
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {allergies.length > 0 && (
              <Alert variant="warning" icon={<AlertTriangle size={16} />} title={t('prescription.allergies.title')}>
                {allergies.map((a) => a.allergen).filter(Boolean).join(' · ')}
              </Alert>
            )}

            {/* The one region on this screen whose content arrives BY ITSELF, a
                second or two after a drug is added, while the dentist is typing
                a dose somewhere else entirely. Without a live region a screen
                reader never mentions that a high-severity interaction appeared —
                and a silently-delivered interaction warning is the same as no
                interaction warning. Polite, not assertive: it must not interrupt
                mid-word, and the ثبت button is never blocked either way. */}
            <Panel
              title={t('prescription.interactions.title')}
              aria-live="polite"
              aria-busy={sweep.loading || undefined}
            >
              {!sweepable && (
                <div style={{ fontSize: 12.5, color: C.textMuted }}>
                  {t('prescription.interactions.needsTwo')}
                </div>
              )}

              {sweepable && sweep.loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spinner size={16} />
                  <span style={{ fontSize: 12.5, color: C.textMuted }}>
                    {t('prescription.interactions.checking')}
                  </span>
                </div>
              )}

              {sweepable && !sweep.loading && sweep.failure === 'credits' && (
                <Alert variant="info" title={t('errors.credits.title')}>
                  {t('errors.credits.body')}
                </Alert>
              )}

              {sweepable && !sweep.loading && sweep.failure === 'unavailable' && (
                <div style={{ fontSize: 12.5, color: C.textMuted }}>
                  {t('prescription.interactions.unavailable')}
                </div>
              )}

              {sweepable && !sweep.loading && !sweep.failure && warnings.length === 0 && (
                <div style={{ fontSize: 12.5, color: CT.goodInk }}>
                  {t('prescription.interactions.none')}
                </div>
              )}

              {warnings.map((w, i) => {
                const sev = severityKey(w.severity);
                const tint = SEVERITY_TINT[sev];
                const typeKey = warningTypeKey(w.warningType);
                return (
                  <div
                    key={`${w.drugPair || w.drug || 'w'}-${i}`}
                    style={{
                      background: tint.bg,
                      color: tint.ink,
                      borderRadius: 10,
                      padding: '10px 12px',
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <strong style={{ fontSize: 11.5, fontWeight: 800 }}>
                        {t(`prescription.interactions.severity.${sev}`)}
                      </strong>
                      {typeKey && (
                        <span style={{ fontSize: 11 }}>
                          · {t(`prescription.interactions.type.${typeKey}`)}
                        </span>
                      )}
                      {w.drugPair && (
                        <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: 11, marginInlineStart: 'auto' }}>
                          {w.drugPair}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>{w.description}</div>
                  </div>
                );
              })}
            </Panel>

            <Panel title={t('prescription.history.title')}>
              {(patient.medications || []).length === 0 ? (
                <div style={{ fontSize: 12.5, color: C.textMuted }}>{t('prescription.history.empty')}</div>
              ) : (
                (patient.medications || []).slice(0, 8).map((m) => (
                  <div key={m.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{m.display}</div>
                    <div style={{ fontSize: 11.5, color: C.textMuted }}>
                      {m.authoredOn
                        ? t('patient.history.prescribedOn', { date: formatDate(m.authoredOn, 'numeric') })
                        : ''}
                    </div>
                  </div>
                ))
              )}
            </Panel>

            {items.length > 0 && (
              <Card>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.9 }}>
                  {items.map((item) => (
                    <div key={item.key} style={{ marginBottom: 4 }}>
                      <Badge variant="outline">{item.drug.name}</Badge>{' '}
                      <span>{dosageText(item)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionScreen;
